import {
    Box,
    Button,
    Drawer,
    Theme,
    Typography,
    useTheme,
    alpha
} from "@mui/material";
import {
    Order,
    PaginationObj,
    SortingObj,
    TableHeaderData
} from "../../types";
import { FilterIcon } from "../../../../assets/SvgIcons";
import CustomFilter from "../../common/CustomFilter";
import CustomTable from "../../common/customTable";
import DataTable from "./DataTable";
import { HoldingDetailsTableData, HoldingDetailsTableObj } from "../types";
import { HoldingDetailsLoading } from "../LoadingComponents";
import { NO_DATA_ERROR_MESSAGE } from "../../../../utils/constant";

const initialSelectedTableHeadings: string[] = [
    "units",
    "investedValue",
    "exposure",
    "marketValue",
    "week52High",
    "marketCapitalisation",
    "dividend"
]

interface DirectEquityHoldingDetailsProps {
    isLoading: boolean;
    sortingObj: SortingObj;
    setSortingObj: React.Dispatch<React.SetStateAction<SortingObj>>;
    fetchHoldingDetailsData: ({
        sortItem,
        order,
        currentPage,
        rowsPerPage,
    }: {
        sortItem?: string;
        order?: Order;
        currentPage?: number;
        rowsPerPage?: number;
    }) => void
    paginationObj: PaginationObj;
    setPaginationObj: React.Dispatch<React.SetStateAction<PaginationObj>>;
    tableHeaders: TableHeaderData[]
    setTableHeaders: React.Dispatch<React.SetStateAction<TableHeaderData[]>>;
    appliedTableHeaders: TableHeaderData[];
    setAppliedTableHeaders: React.Dispatch<React.SetStateAction<TableHeaderData[]>>;
    isFilterDrawerOpen: boolean;
    setFilterDrawerOpen: React.Dispatch<boolean>;
    isTableHeadersLoading: boolean;
    isTableDataLoading: boolean;
    tableData: HoldingDetailsTableObj
}

export default function DirectEquityHoldingDetails({
    isLoading,
    sortingObj,
    setSortingObj,
    fetchHoldingDetailsData,
    paginationObj,
    setPaginationObj,
    tableHeaders,
    setTableHeaders,
    appliedTableHeaders,
    setAppliedTableHeaders,
    isFilterDrawerOpen,
    setFilterDrawerOpen,
    isTableHeadersLoading,
    isTableDataLoading,
    tableData
}: DirectEquityHoldingDetailsProps): JSX.Element {

    const theme = useTheme()

    const handleSort = (orderBy: string, order: Order) => {
        setSortingObj({
            order,
            orderBy
        })

        fetchHoldingDetailsData({
            sortItem: orderBy,
            order,
            currentPage: paginationObj.currentPage + 1,
            rowsPerPage: paginationObj.rowsPerPage
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchHoldingDetailsData({
            rowsPerPage: Number(event.target.value),
            sortItem: sortingObj.orderBy,
            order: sortingObj.order,
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setPaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchHoldingDetailsData({
            currentPage: page + 1,
            sortItem: sortingObj.orderBy,
            order: sortingObj.order,
            rowsPerPage: paginationObj.rowsPerPage
        })
    }


    const handleOpenFilterDrawer = () => {
        setTableHeaders(prevState => prevState.map((headerObj: TableHeaderData) => {
            const isFilterApplied = appliedTableHeaders.find(appliedHeaderObj => appliedHeaderObj.id === headerObj.id)?.checked ?? false

            return { ...headerObj, checked: isFilterApplied }
        }))


        setFilterDrawerOpen(true)
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }

    const handleInvestmentDataTableHeaderChange = (value: string, checked: boolean) => {
        setTableHeaders(prevState => prevState.map((headerObj: TableHeaderData) => (headerObj?.category?.toLowerCase() === "investment data" && headerObj.id === value) ? { ...headerObj, checked } : headerObj))
    }

    const handleMarketDataTableHeaderChange = (value: string, checked: boolean) => {
        setTableHeaders(prevState => prevState.map((headerObj: TableHeaderData) => (headerObj?.category?.toLowerCase() === "market data" && headerObj.id === value) ? { ...headerObj, checked } : headerObj))
    }

    const handleMarketRatiosTableHeaderChange = (value: string, checked: boolean) => {
        setTableHeaders(prevState => prevState.map((headerObj: TableHeaderData) => (headerObj?.category?.toLowerCase() === "market ratios" && headerObj.id === value) ? { ...headerObj, checked } : headerObj))
    }

    const handleFiltersClearAll = () => {
        setFilterDrawerOpen(false)

        setAppliedTableHeaders(tableHeaders
            .map((headerObj: TableHeaderData) =>
                ({ ...headerObj, checked: initialSelectedTableHeadings.includes(headerObj.id) }))
            .filter((headerObj: TableHeaderData) => headerObj.checked)
        )

        setTableHeaders(prevState => prevState.map((headerObj: TableHeaderData) =>
            ({ ...headerObj, checked: initialSelectedTableHeadings.includes(headerObj.id) })))
    }

    const handleApplyFilters = () => {
        setFilterDrawerOpen(false)
        setAppliedTableHeaders(prevState => {
            // Checking which of the exiting filters are applied/removed and putting it into filtersApplied
            const existingFiltersApplied = tableHeaders.filter((obj1: TableHeaderData) => prevState.some((obj2: TableHeaderData) => ((obj1.id === obj2.id) && obj1.checked)))

            //Appending new filters applied at the end of the Exisitng Filters Applied
            const newFiltersApplied = [
                ...existingFiltersApplied,
                ...tableHeaders
                    .filter((obj1: TableHeaderData) => !existingFiltersApplied.some(
                        (obj2: TableHeaderData) => obj1.id === obj2.id))
                    .filter((obj: TableHeaderData) => obj.checked)
            ]

            return newFiltersApplied
        })
    }

    const investmentDataTableHeaders = tableHeaders.filter((headerObj: TableHeaderData) => headerObj.category?.toLowerCase() === "investment data")

    const marketDataTableHeaders = tableHeaders.filter((headerObj: TableHeaderData) => headerObj.category?.toLowerCase() === "market data")

    const marketRatiosTableHeaders = tableHeaders.filter((headerObj: TableHeaderData) => headerObj.category?.toLowerCase() === "market ratios")

    const tableHeadersData: TableHeaderData[] = [
        {
            id: 'schemeName',
            label: 'SECURITY NAME',
            align: 'left',
            isSortable: true,
            headerTableCellStyle: {
                position: "sticky",
                left: 0,
                minWidth: "460px",
                background: theme.palette.grey[50],
                // backgroundColor: `${theme.palette.primary[500]} !important`,
                zIndex: 1
            }
        },
        ...appliedTableHeaders,
    ]

    const tableHeaderIds = tableHeadersData.map(obj => obj.id)

    const currentTableData = tableData.data.map(tableObj => {
        const newTableObj: HoldingDetailsTableData = {
            id: tableObj.schemeName,
            schemeName: tableObj.schemeName
        };

        tableHeadersData.forEach(headerObj => {
            if (headerObj.id in tableObj) {
                newTableObj[headerObj.id] = tableObj[headerObj.id];
            }
        });
        return newTableObj;
    });

    return (
        <>
            <Box
                sx={(theme: Theme) => ({
                    padding: "35px",
                    borderRadius: "25px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    border: `3px solid ${theme.palette.stroke.main}`,
                    maxWidth: {
                        xs: "100%",
                        sm: "100%",
                        md: "100%",
                        // lg: "78vw",
                        // xl: "81.5vw"
                    },
                })}
            >
                {(isTableHeadersLoading || isTableDataLoading || isLoading) && (
                    <HoldingDetailsLoading />
                )}
                {(!(isTableHeadersLoading || isTableDataLoading || isLoading) && !tableData.isSuccess) && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%"
                        }}
                    >
                        <Typography
                            variant="body_600"
                            sx={(theme: Theme) => ({
                                fontWeight: 500,
                                lineHeight: "22.5px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {NO_DATA_ERROR_MESSAGE}
                        </Typography>
                    </Box>
                )}
                {(!(isTableHeadersLoading || isTableDataLoading || isLoading) && tableData.isSuccess) && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between"
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({
                                    lineHeight: "25px",
                                    letterSpacing: "0.01px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                Holdings
                            </Typography>
                            <Button
                                variant="text"
                                startIcon={<Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        opacity: (isTableHeadersLoading || isTableDataLoading || isLoading) ? 0.5 : 1
                                    }}
                                >
                                    <FilterIcon />
                                </Box>
                                }
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    padding: "5px",
                                    ...theme.typography.body_500,
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[50]
                                    }
                                })}
                                TouchRippleProps={{
                                    style: {
                                        color: theme.palette.grey[300]
                                    }
                                }}
                                onClick={handleOpenFilterDrawer}
                                disabled={false}
                            >
                                Filters
                            </Button>
                        </Box>
                        <Box
                            sx={{
                                marginTop: "20px",
                                overflowX: "auto",
                                width: "100%",
                            }}
                        >
                            <CustomTable
                                isLoading={false}
                                tableBody={
                                    <DataTable
                                        data={currentTableData}
                                        headerData={tableHeaderIds}
                                    />
                                }
                                tableData={currentTableData}
                                tableHeaderData={tableHeadersData}
                                handleSort={handleSort}
                                tableDataCount={paginationObj.totalRows}
                                rowsPerPage={paginationObj.rowsPerPage}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                handleRowsPerPageChange={handleRowsPerPageChange}
                                handlePageChange={handlePageChange}
                                currentPage={paginationObj.currentPage}
                                order={sortingObj.order}
                                orderBy={sortingObj.orderBy}
                                tableContainerStyles={{
                                    paddingBottom: "20px"
                                }}
                                tableBodyLoaderRowCustomStyles={{
                                    '& td:first-of-type': {
                                        paddingLeft: "0px"
                                    },
                                }}
                                tableHeadRowCustomStyles={{
                                    '& th:first-of-type': {
                                        paddingLeft: "0px",
                                        fontWeight: 700,
                                        ...theme.typography.body_600
                                    }
                                }}
                                tableBodyCustomStyles={{
                                    '& tr:last-child td': {
                                        border: 0
                                    }
                                }}
                                loadingRowsNum={5}
                            />
                        </Box>
                    </>
                )}
            </Box>
            <Drawer
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        padding: "25px",
                        width: "450px"
                    }
                }}
                ModalProps={{
                    sx: (theme: Theme) => ({
                        backgroundColor: `${theme.palette.common.black}03`
                    })
                }}
            >
                <CustomFilter
                    isLoading={isTableHeadersLoading || isTableDataLoading || isLoading}
                    handleClearAll={handleFiltersClearAll}
                    handleApplyFilters={handleApplyFilters}
                    showCloseIcon={true}
                    handleCloseIconClick={handleCloseFilterDrawer}
                    showAmcFilter={false}
                    showCategoryFilter={false}
                    showRiskFilter={false}
                    showInvestmentDataFilter={true}
                    investmentFilterData={investmentDataTableHeaders}
                    handleInvestmentDataFilterChange={handleInvestmentDataTableHeaderChange}
                    showMarketDataFilter={true}
                    marketFilterData={marketDataTableHeaders}
                    handleMarketDataFilterChange={handleMarketDataTableHeaderChange}
                    showMarketRatiosFilter={true}
                    marketRatiosFilterData={marketRatiosTableHeaders}
                    handleMarketRatiosFilterChange={handleMarketRatiosTableHeaderChange}
                    defaultExpandedFilter="investmentData"
                    isFilterApplied={true}
                />
            </Drawer>
        </>
    )
}